import React, { useCallback, useEffect, useState } from "react";
import { Bell, Send, Users, Zap, Trash2 } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";

function AdminNotifications() {
  const [form, setForm] = useState({ title: "", message: "", audience_mode: "all", audience: "all", topic: "all", user_id: "" });
  const [stats, setStats] = useState({ sent_today: 0, total_users: 0 });
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [notificationRes, usersRes] = await Promise.all([
        API.get("/admin/notifications"),
        API.get("/admin/users"),
      ]);
      setStats(notificationRes.data?.stats || {});
      setItems(Array.isArray(notificationRes.data?.notifications) ? notificationRes.data.notifications : []);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch {
      setStats({ sent_today: 0, total_users: 0 });
      setItems([]);
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sendNotification = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.message.trim()) return;
    const payload = {
      title: form.title,
      message: form.message,
      topic: form.topic || "all",
      audience:
        form.audience_mode === "user"
          ? String(form.user_id || "").trim()
          : form.audience_mode === "topic"
            ? "all"
            : "all",
    };
    if (form.audience_mode === "user" && !payload.audience) {
      alert("Select a user first");
      return;
    }
    setSaving(true);
    try {
      await API.post("/admin/notifications", payload);
      setForm({ title: "", message: "", audience_mode: "all", audience: "all", topic: "all", user_id: "" });
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  const deleteNotification = async (id) => {
    if (!window.confirm("Delete this notification?")) return;
    try {
      await API.delete(`/admin/notifications/${id}`);
      await loadData();
    } catch (err) {
      alert("Failed to delete notification");
    }
  };

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div>
            <h1 style={title}>Send Push Notification</h1>
            <p style={subtitle}>Send in-app notifications to all users</p>
          </div>
          <div style={connected}><Zap size={14} /> In-App Connected</div>
        </div>

        <div style={grid}>
          <form style={card} onSubmit={sendNotification}>
            <label style={label}>Notification Title</label>
            <input
              style={input}
              value={form.title}
              maxLength={100}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Result Declared!"
            />
            <span style={counter}>{form.title.length}/100 characters</span>

            <label style={label}>Message</label>
            <textarea
              style={textarea}
              value={form.message}
              maxLength={500}
              onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
              placeholder="Write your notification message here..."
            />
            <span style={counter}>{form.message.length}/500 characters</span>

            <label style={label}>Target Audience</label>
            <div style={audienceGrid}>
              <button type="button" style={form.audience_mode === "all" ? audienceActive : audienceButton} onClick={() => setForm((prev) => ({ ...prev, audience_mode: "all", audience: "all", topic: "all" }))}>
                <Users size={18} />
                <span><b>All Users</b><small>Broadcast to everyone</small></span>
              </button>
              <button type="button" style={form.audience_mode === "topic" ? audienceActive : audienceButton} onClick={() => setForm((prev) => ({ ...prev, audience_mode: "topic", audience: "all", topic: "results" }))}>
                <Bell size={18} />
                <span><b>Result Topic</b><small>Subscribed to results</small></span>
              </button>
              <button type="button" style={form.audience_mode === "user" ? audienceActive : audienceButton} onClick={() => setForm((prev) => ({ ...prev, audience_mode: "user", audience: "", user_id: prev.user_id || "" }))}>
                <Zap size={18} />
                <span><b>Single User</b><small>Send to one player</small></span>
              </button>
            </div>

            {form.audience_mode === "user" && (
              <>
                <label style={label}>Select User</label>
                <select
                  style={input}
                  value={form.user_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, user_id: e.target.value, audience: e.target.value }))}
                >
                  <option value="">Choose user</option>
                  {users.slice(0, 1000).map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name || `User ${user.id}`} - {user.phone || user.id}
                    </option>
                  ))}
                </select>
              </>
            )}

            <label style={label}>Preview</label>
            <div style={previewShell}>
              <div style={previewCard}>
                <span style={previewIcon}><Bell size={18} /></span>
                <div>
                  <strong>{form.title || "Notification Title"}</strong>
                  <p>{form.message || "Your notification message will appear here..."}</p>
                  <small>
                    {form.audience_mode === "user"
                      ? `User ${form.user_id || "-" } - Matka Kings`
                      : form.audience_mode === "topic"
                        ? "Result Topic - Matka Kings"
                        : "All Users - Matka Kings"}
                  </small>
                </div>
              </div>
            </div>

            <button type="submit" style={sendButton} disabled={saving || !form.title.trim() || !form.message.trim()}>
              <Send size={18} />
              {saving ? "Sending..." : form.audience_mode === "user" ? "Send to User" : form.audience_mode === "topic" ? "Send to Result Topic" : "Send to All Users"}
            </button>
          </form>

          <aside style={side}>
            <article style={statCard}>
              <h3><Zap size={16} /> Statistics</h3>
              <div style={statRow}><span>Sent Today</span><b>{Number(stats.sent_today || 0)}</b></div>
              <div style={statRow}><span>Total Users</span><b>{Number(stats.total_users || 0).toLocaleString("en-IN")}</b></div>
            </article>
            <article style={infoCard}>
              <h3><Bell size={16} /> Recent Notifications</h3>
              {items.slice(0, 6).map((item) => (
                <div key={item.id} style={itemRow}>
                  <div style={itemContent}>
                    <b>{item.title}</b>
                    <span>{item.message}</span>
                  </div>
                  <button type="button" style={deleteButton} onClick={() => deleteNotification(item.id)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {!items.length && <p style={empty}>No notifications sent yet</p>}
            </article>
          </aside>
        </div>
      </section>
    </AdminLayout>
  );
}

const page = { display: "grid", gap: 18 };
const header = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" };
const title = { margin: 0, fontSize: 22, fontWeight: 900 };
const subtitle = { margin: "6px 0 0", color: "#64748b", fontSize: 13 };
const connected = { height: 38, padding: "0 14px", border: "1px solid #86efac", background: "#f0fdf4", color: "#15803d", display: "flex", alignItems: "center", gap: 8, fontWeight: 700, borderRadius: 8 };
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: 14, maxWidth: 860, margin: "0 auto", width: "100%" };
const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16, display: "grid", gap: 10, boxShadow: "0 8px 22px rgba(15,23,42,0.05)" };
const label = { fontSize: 13, fontWeight: 800, color: "#0f172a", marginTop: 8 };
const input = { height: 46, border: "1px solid #d8dee8", borderRadius: 10, padding: "0 14px", outline: 0, fontSize: 14 };
const textarea = { minHeight: 88, border: "1px solid #d8dee8", borderRadius: 10, padding: 14, outline: 0, resize: "vertical", fontSize: 14, fontFamily: "inherit" };
const counter = { fontSize: 11, color: "#64748b" };
const audienceGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 };
const audienceButton = { border: "1px solid #dbe4f0", background: "#fff", minHeight: 64, borderRadius: 10, padding: "0 14px", display: "flex", alignItems: "center", gap: 12, textAlign: "left", color: "#334155" };
const audienceActive = { ...audienceButton, borderColor: "#2563eb", background: "#eff6ff", color: "#1d4ed8" };
const previewShell = { background: "#111827", padding: 16, borderRadius: 10 };
const previewCard = { background: "#fff", minHeight: 72, padding: 14, display: "flex", alignItems: "center", gap: 14 };
const previewIcon = { width: 40, height: 40, background: "#2563eb", color: "#fff", display: "grid", placeItems: "center" };
const sendButton = { height: 54, border: 0, borderRadius: 10, background: "linear-gradient(90deg,#60a5fa,#8b5cf6)", color: "#fff", fontSize: 16, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 };
const side = { display: "grid", gap: 16, alignContent: "start" };
const statCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 };
const infoCard = { ...statCard, background: "#eff6ff", borderColor: "#bfdbfe" };
const statRow = { height: 34, display: "flex", alignItems: "center", justifyContent: "space-between", color: "#475569" };
const itemRow = { display: "flex", alignItems: "center", gap: 8, padding: "10px 0", borderTop: "1px solid #dbeafe" };
const itemContent = { flex: 1, display: "grid", gap: 3, fontSize: 12 };
const deleteButton = { width: 36, height: 36, border: 0, background: "#fee2e2", color: "#dc2626", borderRadius: 10, display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0 };
const empty = { margin: 0, color: "#64748b" };

export default AdminNotifications;
